import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import config from "./config";
import { colorNameToHex, parseColorName, extractColorsFromText } from "./utils/colorUtils";

type Toast = {
  id: number;
  message: string;
  type: 'success' | 'error';
};

type ProductDto = {
  id: number;
  name: string;
  imageUrl: string;
  salePrice: number;
  lastUpdated: string;
  description?: string;
  brand?: string;
  model?: string;
  retailer?: string;
  availableSizes?: string[];
  size?: string;
  width?: string;
};

type NotifyListResultDTO = {
  notificationId: number;
  notificationUpdatedDate: string;
  product: ProductDto;
  enabled?: boolean;
  selectedSize?: string;
  selectedWidth?: string;
  size?: string; // From API response
  width?: string; // From API response
};

const NotifyList: React.FC = () => {
  const { getAccessTokenSilently, user, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();
  const [notifyList, setNotifyList] = useState<NotifyListResultDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [toasts, setToasts] = useState<Toast[]>([]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const addToast = (message: string, type: 'success' | 'error') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    // Auto-remove toast after 4 seconds
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 4000);
  };

  useEffect(() => {
    const fetchNotifyList = async () => {
      if (!user) {
        loginWithRedirect();
        return;
      }
      try {
        const token = await getAccessTokenSilently();
        const res = await fetch(`${config.apiBaseUrl}/api/notifylist`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) throw new Error("Failed to fetch notify list");
        const data = await res.json();
        console.log('Notify list data:', data); // Debug log
        
        // Map Size/Width from API to selectedSize/selectedWidth
        const mappedData = data.map((item: any) => ({
          ...item,
          selectedSize: item.selectedSize || item.size || '',
          selectedWidth: item.selectedWidth || item.width || ''
        }));
        
        setNotifyList(mappedData);
        setSelectedIndex(0); // Show first row details on load
      } catch {
        setNotifyList([]);
      } finally {
        setLoading(false);
      }
    };
    fetchNotifyList();
    // eslint-disable-next-line
  }, [user]);

  // Sync dropdowns when a notification item is selected
  useEffect(() => {
    if (notifyList.length > 0 && selectedIndex < notifyList.length) {
      const selectedItem = notifyList[selectedIndex];
      console.log('Selected item:', selectedItem.notificationId, 'Size:', selectedItem.selectedSize, 'Width:', selectedItem.selectedWidth);
    }
  }, [selectedIndex, notifyList]);

  const handleToggle = async (notificationId: number, checked: boolean) => {
    try {
      const token = await getAccessTokenSilently();
      await fetch(`${config.apiBaseUrl}/api/notifylist/${notificationId}/toggle`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ enabled: checked }),
      });
      setNotifyList(list =>
        list.map(item =>
          item.notificationId === notificationId ? { ...item, enabled: checked } : item
        )
      );
    } catch {
      addToast("Failed to update notification status.", "error");
    }
  };

  const handleDelete = async (notificationId: number) => {
    try {
      const token = await getAccessTokenSilently();
      await fetch(`${config.apiBaseUrl}/api/notifylist/${notificationId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setNotifyList(list => list.filter(item => item.notificationId !== notificationId));
      // If deleted row was selected, select first row or none
      setSelectedIndex(prev =>
        prev >= notifyList.length - 1 ? 0 : prev
      );
    } catch {
      addToast("Failed to delete notification.", "error");
    }
  };

  const handleSizeChange = (notificationId: number, size: string) => {
    setNotifyList(list =>
      list.map(item =>
        item.notificationId === notificationId ? { ...item, selectedSize: size } : item
      )
    );
  };

  const handleWidthChange = (notificationId: number, width: string) => {
    setNotifyList(list =>
      list.map(item =>
        item.notificationId === notificationId ? { ...item, selectedWidth: width } : item
      )
    );
  };

  const getSizesArray = (product: ProductDto): string[] => {
    // Check availableSizes first
    if (product.availableSizes) {
      if (typeof product.availableSizes === 'string') {
        return (product.availableSizes as string).split(',').map((s: string) => s.trim()).filter((s: string) => s);
      }
      if (Array.isArray(product.availableSizes)) {
        return product.availableSizes as string[];
      }
    }
    
    // Fallback: parse the size field
    if (product.size && typeof product.size === 'string') {
      return product.size.split(',').map((s: string) => s.trim()).filter((s: string) => s);
    }
    
    return [];
  };

  const getWidthsArray = (product: ProductDto): string[] => {
    // Parse the width field
    if (product.width && typeof product.width === 'string') {
      return product.width.split(',').map((w: string) => w.trim()).filter((w: string) => w);
    }
    
    return [];
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '60vh',
        fontSize: '1.2rem',
        color: '#666',
        padding: '40px 20px'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px'
        }}>
          {/* Loading Spinner */}
          <div style={{
            width: '50px',
            height: '50px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #2c5aa0',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          
          {/* Loading Text */}
          <div style={{
            fontSize: '1.1rem',
            fontWeight: '500'
          }}>
            Loading Notify List...
          </div>
          
          {/* Animated Dots */}
          <div style={{
            fontSize: '1.5rem',
            letterSpacing: '2px',
            animation: 'dots 1.5s infinite'
          }}>
            • • •
          </div>
        </div>
        
        {/* CSS Animation Styles */}
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @keyframes dots {
            0%, 20% { opacity: 0; }
            50% { opacity: 1; }
            100% { opacity: 0; }
          }
        `}</style>
      </div>
    );
  }

  const selectedItem = notifyList[selectedIndex];

  return (
    <div style={{ 
      maxWidth: 1200, 
      margin: "0 auto", 
      padding: isMobile ? 12 : 24, 
      display: "flex", 
      flexDirection: isMobile ? "column" : "row",
      gap: isMobile ? 16 : 32 
    }}>
      <div style={{ flex: isMobile ? "1" : "2" }}>
        <button
          onClick={() => navigate('/')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 20px',
            marginBottom: '24px',
            backgroundColor: '#2563eb',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => (e.target as HTMLButtonElement).style.backgroundColor = '#1d4ed8'}
          onMouseOut={(e) => (e.target as HTMLButtonElement).style.backgroundColor = '#2563eb'}
        >
          ← Back to Home
        </button>
        <h2 style={{ textAlign: 'left', fontWeight: 'bold', fontSize: isMobile ? '1.5rem' : '2rem', marginBottom: isMobile ? '16px' : '24px' }}>Notify List</h2>
        {notifyList.length === 0 ? (
          <p style={{ textAlign: 'left' }}>Your notification list is empty.</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "600px" }}>
              <thead>
                <tr style={{ background: "#f3f4f6" }}>
                  <th style={{ padding: 8, border: "1px solid #ddd", textAlign: 'left' }}>Image</th>
                  <th style={{ padding: 8, border: "1px solid #ddd", textAlign: 'left' }}>Product Name</th>
                  <th style={{ padding: 8, border: "1px solid #ddd", textAlign: 'left' }}>Price</th>
                  <th style={{ padding: 8, border: "1px solid #ddd", textAlign: 'left' }}>Size</th>
                  <th style={{ padding: 8, border: "1px solid #ddd", textAlign: 'left' }}>Width</th>
                  <th style={{ padding: 8, border: "1px solid #ddd", textAlign: 'left' }}>Last Updated</th>
                  <th style={{ padding: 8, border: "1px solid #ddd", textAlign: 'left' }}>Enabled</th>
                  <th style={{ padding: 8, border: "1px solid #ddd", textAlign: 'left' }}>Actions</th>
                </tr>
              </thead>
            <tbody>
              {notifyList.map((item, idx) => (
                <tr
                  key={item.notificationId}
                  style={{
                    background: idx === selectedIndex ? "#e0e7ff" : undefined,
                    cursor: "pointer"
                  }}
                  onClick={() => setSelectedIndex(idx)}
                >
                  <td style={{ padding: 8, border: "1px solid #ddd", textAlign: "left" }}>
                    <img
                      src={item.product.imageUrl}
                      alt={item.product.name}
                      style={{ width: 60, height: 60, objectFit: "cover", borderRadius: 4 }}
                    />
                  </td>
                  <td style={{ padding: 8, border: "1px solid #ddd", textAlign: "left" }}>{item.product.name}</td>
                  <td style={{ padding: 8, border: "1px solid #ddd", textAlign: "left" }}>
                    ${item.product.salePrice?.toFixed(2)}
                  </td>
                  <td style={{ padding: 8, border: "1px solid #ddd", textAlign: "left" }}>
                    {item.selectedSize || '-'}
                  </td>
                  <td style={{ padding: 8, border: "1px solid #ddd", textAlign: "left" }}>
                    {item.selectedWidth || '-'}
                  </td>
                  <td style={{ padding: 8, border: "1px solid #ddd", textAlign: "left" }}>
                    {new Date(item.notificationUpdatedDate).toLocaleString()}
                  </td>
                  <td style={{ padding: 8, border: "1px solid #ddd", textAlign: "left" }}>
                    <input
                      type="checkbox"
                      checked={item.enabled ?? true}
                      onChange={e => handleToggle(item.notificationId, e.target.checked)}
                      onClick={e => e.stopPropagation()}
                    />
                  </td>
                  <td style={{ padding: 8, border: "1px solid #ddd", textAlign: "left" }}>
                    <button
                      style={{
                        background: "#ef4444",
                        color: "#fff",
                        border: "none",
                        borderRadius: 4,
                        padding: "6px 12px",
                        cursor: "pointer",
                        fontWeight: 600,
                      }}
                      onClick={e => {
                        e.stopPropagation();
                        handleDelete(item.notificationId);
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
      <div style={{
        flex: isMobile ? "1" : "1",
        background: "#f9fafb",
        borderRadius: 8,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        padding: isMobile ? 16 : 24,
        minHeight: 300
      }}>
        <h3 style={{ textAlign: 'left', fontWeight: 'bold', fontSize: '1.5rem', marginBottom: '20px' }}>Notification Details</h3>
        {selectedItem ? (
          <div style={{ textAlign: 'left' }}>
            <div style={{ 
              marginTop: '0px', 
              padding: '16px', 
              backgroundColor: '#fff', 
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <label style={{ 
                display: 'block',
                fontWeight: 'bold', 
                marginBottom: '8px',
                color: '#374151'
              }}>
                Size Preference (Optional)
              </label>
              <select
                value={selectedItem.selectedSize || ''}
                onChange={(e) => handleSizeChange(selectedItem.notificationId, e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  backgroundColor: '#fff',
                  cursor: 'pointer'
                }}
              >
                <option value="">-- Select a size --</option>
                {(() => {
                  const sizes = getSizesArray(selectedItem.product);
                  return sizes.length > 0 ? (
                    sizes.map((size: string) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))
                  ) : (
                    <option disabled>No sizes available</option>
                  );
                })()}
              </select>
              <p style={{
                fontSize: '12px',
                color: '#6b7280',
                marginTop: '6px',
                marginBottom: '0'
              }}>
                Select your preferred size for this product
              </p>
            </div>
            <div style={{ 
              marginTop: '20px', 
              padding: '16px', 
              backgroundColor: '#fff', 
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <label style={{ 
                display: 'block',
                fontWeight: 'bold', 
                marginBottom: '8px',
                color: '#374151'
              }}>
                Width Preference (Optional)
              </label>
              <select
                value={selectedItem.selectedWidth || ''}
                onChange={(e) => handleWidthChange(selectedItem.notificationId, e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  backgroundColor: '#fff',
                  cursor: 'pointer'
                }}
              >
                <option value="">-- Select a width --</option>
                {(() => {
                  const widths = getWidthsArray(selectedItem.product);
                  return widths.length > 0 ? (
                    widths.map((width: string) => (
                      <option key={width} value={width}>
                        {width}
                      </option>
                    ))
                  ) : (
                    <option disabled>No widths available</option>
                  );
                })()}
              </select>
              <p style={{
                fontSize: '12px',
                color: '#6b7280',
                marginTop: '6px',
                marginBottom: '0'
              }}>
                Select your preferred width for this product
              </p>
            </div>
            <img
              src={selectedItem.product.imageUrl}
              alt={selectedItem.product.name}
              style={{
                width: 120,
                height: 120,
                objectFit: "cover",
                borderRadius: 8,
                marginBottom: 16,
                marginTop: 16,
                background: "#eee",
                display: 'block'
              }}
            />
            <h4 style={{ textAlign: 'left', fontWeight: 'bold', fontSize: '1.3rem', color: '#2c5aa0', marginBottom: '20px' }}>{selectedItem.product.name}</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '12px', marginBottom: '16px' }}>
              <div style={{ textAlign: 'left' }}><strong>Price:</strong></div>
              <div style={{ textAlign: 'left' }}>${selectedItem.product.salePrice?.toFixed(2)}</div>
              <div style={{ textAlign: 'left' }}><strong>Last Updated:</strong></div>
              <div style={{ textAlign: 'left' }}>{new Date(selectedItem.notificationUpdatedDate).toLocaleString()}</div>
            </div>
            
            {/* Display Available Colors as Swatches */}
            {(() => {
              const colors = extractColorsFromText(selectedItem.product.description || '');
              if (colors.length > 0) {
                return (
                  <div style={{ textAlign: 'left', marginTop: '16px' }}>
                    <strong>Color Swatches:</strong>
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '12px',
                      marginTop: '10px'
                    }}>
                      {colors.map((color: string, index: number) => (
                        <div
                          key={index}
                          title={color}
                          style={{
                            cursor: 'pointer'
                          }}
                        >
                          {/* Color Circle */}
                          <div
                            style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '50%',
                              backgroundColor: parseColorName(color),
                              border: '2px solid #ddd',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                              transition: 'transform 0.2s, box-shadow 0.2s',
                              cursor: 'pointer'
                            }}
                            onMouseEnter={(e) => {
                              (e.currentTarget as HTMLElement).style.transform = 'scale(1.1)';
                              (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
                            }}
                            onMouseLeave={(e) => {
                              (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
                              (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }
              return null;
            })()}
            
            <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '12px', marginTop: '16px' }}>
              <div style={{ textAlign: 'left' }}><strong>Brand:</strong></div>
              <div style={{ textAlign: 'left' }}>{selectedItem.product.brand}</div>
              <div style={{ textAlign: 'left' }}><strong>Model:</strong></div>
              <div style={{ textAlign: 'left' }}>{selectedItem.product.model}</div>
              <div style={{ textAlign: 'left' }}><strong>Retailer:</strong></div>
              <div style={{ textAlign: 'left' }}>{selectedItem.product.retailer}</div>
            </div>
            
            <div style={{ textAlign: 'left', marginTop: '20px' }}>
              <strong>Description:</strong>
              <div 
                style={{ marginTop: '8px', lineHeight: '1.6' }}
                dangerouslySetInnerHTML={{ __html: (selectedItem.product.description || '').replace(/Available Colors:[^<]*(<[^>]*>)*[^<]*/gi, '').replace(/<p><br\/><\/p>/g, '') }}
              />
            </div>
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={async () => {
                  try {
                    const token = await getAccessTokenSilently();
                    await fetch(`${config.apiBaseUrl}/api/notifylist/${selectedItem.notificationId}/update-preferences`, {
                      method: "PATCH",
                      headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                      },
                      body: JSON.stringify({
                        selectedSize: selectedItem.selectedSize || '',
                        selectedWidth: selectedItem.selectedWidth || ''
                      }),
                    });
                    addToast("Preferences updated successfully!", "success");
                  } catch (error) {
                    addToast("Failed to update preferences.", "error");
                  }
                }}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  backgroundColor: '#16a34a',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => (e.target as HTMLButtonElement).style.backgroundColor = '#15803d'}
                onMouseOut={(e) => (e.target as HTMLButtonElement).style.backgroundColor = '#16a34a'}
              >
                Update Preferences
              </button>
              <button
                onClick={() => navigate(`/product/${selectedItem.product.id}`)}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  backgroundColor: '#2563eb',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => (e.target as HTMLButtonElement).style.backgroundColor = '#1d4ed8'}
                onMouseOut={(e) => (e.target as HTMLButtonElement).style.backgroundColor = '#2563eb'}
              >
                View Full Details
              </button>
            </div>
          </div>
        ) : (
          <p style={{ textAlign: 'left' }}>Select a notification to see the details</p>
        )}
      </div>

      {/* Toast Notification Container */}
      <div style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        maxWidth: isMobile ? '90%' : '400px',
      }}>
        {toasts.map(toast => (
          <div
            key={toast.id}
            style={{
              padding: '14px 18px',
              borderRadius: '8px',
              backgroundColor: toast.type === 'success' ? '#4CAF50' : '#f44336',
              color: 'white',
              fontSize: '14px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              animation: 'slideIn 0.3s ease-out',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            {toast.type === 'success' ? (
              <span style={{ fontSize: '18px' }}>✓</span>
            ) : (
              <span style={{ fontSize: '18px' }}>✕</span>
            )}
            {toast.message}
          </div>
        ))}
      </div>

      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default NotifyList;